import React, {useState} from 'react'
import {motion} from 'framer-motion'
import SafeIcon from '../../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'
import toast from 'react-hot-toast'

const {FiMail, FiArrowRight, FiCheck} = FiIcons

const NewsletterSection = ({content = {}, onCTAClick}) => {
  const [email, setEmail] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSubscribed, setIsSubscribed] = useState(false)

  const {
    title = "No Bull-Shit Nutrition",
    subtitle = "Get weekly evidence-based nutrition tips that actually work in the real world",
    placeholder = "Enter your email address",
    buttonText = "Get Tips",
    maierliteFormId = "your-mailerlite-form-id"
  } = content

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!email) return

    setIsSubmitting(true)
    try {
      // Track newsletter subscription attempt
      onCTAClick?.('newsletter_subscribe', 'newsletter_signup')

      // For demo purposes, we'll simulate a successful subscription
      // In production, you would integrate with MailerLite API
      await new Promise(resolve => setTimeout(resolve, 1000))

      setIsSubscribed(true)
      toast.success('Successfully subscribed! Check your email for confirmation.')

      // Track successful subscription
      onCTAClick?.('newsletter_subscribe_success', 'newsletter_confirmation')
    } catch (error) {
      toast.error('Something went wrong. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <section className="py-20 bg-gray-900 text-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{opacity: 0, y: 30}}
          whileInView={{opacity: 1, y: 0}}
          transition={{duration: 0.8}}
          viewport={{once: true}}
          className="text-center"
        >
          {/* Header */}
          <div className="mb-8">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              {title}
            </h2>
            <p className="text-xl text-gray-300 max-w-2xl mx-auto">
              {subtitle}
            </p>
          </div>

          {/* Benefits */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            {[
              "Weekly evidence-based tips",
              "No fad diets or quick fixes", 
              "Real-world practical advice"
            ].map((benefit, index) => (
              <motion.div
                key={index}
                initial={{opacity: 0, y: 20}}
                whileInView={{opacity: 1, y: 0}}
                transition={{duration: 0.6, delay: index * 0.1}}
                viewport={{once: true}}
                className="flex items-center justify-center space-x-2 text-gray-300"
              >
                <SafeIcon icon={FiCheck} className="w-5 h-5 text-nutrition-500 flex-shrink-0" />
                <span>{benefit}</span>
              </motion.div>
            ))}
          </div>

          {/* Newsletter Form */}
          {!isSubscribed ? (
            <motion.form
              initial={{opacity: 0, y: 20}}
              whileInView={{opacity: 1, y: 0}}
              transition={{duration: 0.8, delay: 0.3}}
              viewport={{once: true}}
              onSubmit={handleSubmit}
              className="max-w-md mx-auto"
            >
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex-1 relative">
                  <SafeIcon icon={FiMail} className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={placeholder}
                    required
                    className="w-full pl-12 pr-4 py-4 bg-white text-gray-900 rounded-lg focus:outline-none focus:ring-2 focus:ring-nutrition-500 placeholder-gray-500"
                  />
                </div>
                <button
                  type="submit"
                  disabled={isSubmitting || !email}
                  className="bg-nutrition-600 hover:bg-nutrition-700 text-white px-8 py-4 rounded-lg font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2 whitespace-nowrap"
                >
                  {isSubmitting ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <>
                      <span>{buttonText}</span>
                      <SafeIcon icon={FiArrowRight} className="w-4 h-4" />
                    </>
                  )}
                </button>
              </div>
              <p className="text-sm text-gray-400 mt-4">
                No spam, ever. Unsubscribe anytime. We respect your privacy.
              </p>
            </motion.form>
          ) : (
            <motion.div
              initial={{opacity: 0, scale: 0.8}}
              animate={{opacity: 1, scale: 1}}
              transition={{duration: 0.5}}
              className="max-w-md mx-auto bg-nutrition-600 rounded-lg p-8"
            >
              <SafeIcon icon={FiCheck} className="w-16 h-16 text-white mx-auto mb-4" />
              <h3 className="text-2xl font-bold mb-2">You're In!</h3>
              <p className="text-nutrition-100">
                Check your email for a confirmation link and your first dose of no-BS nutrition wisdom.
              </p>
            </motion.div>
          )}

          {/* Social Proof */}
          <motion.div
            initial={{opacity: 0}}
            whileInView={{opacity: 1}}
            transition={{duration: 0.8, delay: 0.5}}
            viewport={{once: true}}
            className="mt-12 text-center"
          >
            <div className="flex items-center justify-center space-x-2 text-sm text-gray-400">
              <div className="flex -space-x-2">
                {[
                  "https://images.unsplash.com/photo-1494790108755-2616b612b786?w=32&h=32&fit=crop&crop=face",
                  "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=32&h=32&fit=crop&crop=face",
                  "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=32&h=32&fit=crop&crop=face"
                ].map((src, index) => (
                  <img
                    key={index}
                    src={src}
                    alt=""
                    className="w-8 h-8 rounded-full border-2 border-gray-900"
                  />
                ))}
              </div>
              <span>Join 10,000+ subscribers getting real nutrition advice</span>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  )
}

export default NewsletterSection